import {
  Biohazard,
  Blend,
  Bridge,
  CalendarClock,
  Castle,
  Compass,
  Construction,
  CornerUpRight,
  Cpu,
  Crosshair,
  Drone,
  EyeOff,
  Expand,
  Feather,
  Fence,
  Flame,
  Footprints,
  Gauge,
  Grid3x3,
  Hourglass,
  IdCard,
  Landmark,
  Layers,
  type LucideIcon,
  Megaphone,
  Move,
  MoveUp,
  Network,
  PlaneLanding,
  Plane,
  RadioTower,
  Radar,
  Rabbit,
  Rocket,
  Route,
  Rows2,
  Scale,
  ShieldAlert,
  Spline,
  Target,
  Truck,
  Turtle,
  Users,
  BookOpen,
  Zap,
} from "lucide-react";

import type { CustomCapacity, CustomCapacityLevel, CustomEnemySheet, CustomManoeuvre } from "../../api/types";
import { k } from "../../i18n/i18n";
import { sidcOf } from "../orbats/sidc";
import type { StepKey } from "./sheet";

/** Pictogrammes de l'assistant « ENI personnalisé » (demande d'Alexis du 28/10/2026 : rendre la vue plus
 *  attrayante) : une icône par étape, par inspiration, par niveau technologique, par capacité et par choix de
 *  doctrine, et le symbole APP-6 ennemi du type de forces dominant (fonctions de la palette APP-6, identité
 *  hostile). Sobres et lisibles : l'icône double le libellé, jamais ne le remplace. */

export const STEP_ICONS: Record<StepKey, LucideIcon> = {
  identite: IdCard,
  forces: Layers,
  capacites: Crosshair,
  doctrine: BookOpen,
  forces_faiblesses: Scale,
  modes: Route,
};

/** Phrase d'introduction de chaque étape, sous son titre. */
export const STEP_LEADS: Record<StepKey, string> = {
  identite: k("Son nom de code, son inspiration et ce qui le caractérise."),
  forces: k("Son ordre de bataille, la nature de ses forces et le niveau de ses matériels."),
  capacites: k("Le niveau de chacune de ses capacités de combat, d'appui et de soutien."),
  doctrine: k("Sa manière de commander, de manœuvrer, de tirer et de se défendre."),
  forces_faiblesses: k("Ce qui le rend redoutable, ce qui le rend vulnérable, et vos documents de doctrine."),
  modes: k("Les quatorze modes d'action tirés de la fiche, à relire avant de valider l'ENI."),
};

/** Ce qui est obligatoire à chaque étape (le reste garde sa valeur proposée). */
export const STEP_REQUIRED: Record<StepKey, string> = {
  identite: k("Obligatoire : le nom de code. Le reste est facultatif."),
  forces: k("Rien d'obligatoire : chaque choix part d'une valeur médiane, à ajuster."),
  capacites: k("Rien d'obligatoire : chaque capacité part du niveau moyen, à ajuster."),
  doctrine: k("Obligatoire : une ou deux manœuvres préférées. Le reste part d'une valeur médiane."),
  forces_faiblesses: k("Facultatif : ces textes et documents affinent les modes d'action."),
  modes: k("Obligatoire : composer les modes d'action, les relire, puis valider l'ENI."),
};

export const INSPIRATION_ICONS: Record<CustomEnemySheet["identite"]["inspiration"], LucideIcon> = {
  conventionnel: Landmark,
  hybride: Blend,
  irregulier: Footprints,
  fictif: Feather,
};

export const TECHNO_ICONS: Record<CustomEnemySheet["forces"]["niveau_techno"], LucideIcon> = {
  ancien: Hourglass,
  intermediaire: Gauge,
  moderne: Cpu,
};

export const CAPACITY_ICONS: Record<CustomCapacity, LucideIcon> = {
  artillerie_lrm: Rocket,
  drones: Drone,
  guerre_electronique: RadioTower,
  sol_air: Radar,
  appui_aerien: Plane,
  genie_franchissement: Bridge,
  genie_obstacles: Construction,
  logistique: Truck,
  nrbc: Biohazard,
  forces_speciales: Target,
  milices: Users,
  action_informationnelle: Megaphone,
};

/** Teinte du pictogramme d'une capacité selon son niveau : rouge ennemi de plus en plus marqué. */
export const CAPACITY_TINTS: Record<CustomCapacityLevel, string> = {
  absent: "border-line bg-canvas text-ink-muted",
  faible: "border-hostile-soft bg-surface text-hostile",
  moyen: "border-hostile-soft bg-hostile-soft text-hostile",
  fort: "border-hostile bg-hostile text-white",
};

export const MANOEUVRE_ICONS: Record<CustomManoeuvre, LucideIcon> = {
  frontale: MoveUp,
  debordement: CornerUpRight,
  infiltration: Spline,
  enveloppement_vertical: PlaneLanding,
};

/** Icône de chaque option des choix de doctrine (`DOCTRINE_CHOICES`), par champ puis par valeur. */
export const DOCTRINE_ICONS: Record<string, Record<string, LucideIcon>> = {
  commandement: { centralise: Network, intention: Compass },
  echelonnement: { deux_echelons: Rows2, echelon_reserve: Layers, disperse: Expand },
  feux: { massifs_preparatoires: Flame, precis_a_la_demande: Crosshair },
  defense: { zone: Grid3x3, mobile: Move, ferme: Castle },
  contre_attaque: { immediate: Zap, planifiee: CalendarClock, tardive: Hourglass },
  population: { ignoree: EyeOff, exploitee: Users, boucliers: ShieldAlert },
};

/** Icônes des deux bouts de chaque échelle de doctrine (1 à 5). */
export const SCALE_ICONS: Record<"tempo" | "choc_usure" | "obstacles", { low: LucideIcon; high: LucideIcon }> = {
  tempo: { low: Turtle, high: Rabbit },
  choc_usure: { low: Zap, high: Hourglass },
  obstacles: { low: Route, high: Fence },
};

/** Fonction APP-6 représentative de chaque type de forces dominant (entrées de la palette APP-6) : chars, infanterie
 *  motorisée, infanterie légère, infanterie parachutiste, interarmes. */
const TYPE_APP6: Record<CustomEnemySheet["forces"]["type_dominant"], string> = {
  blinde_mecanise: "10-120500-00-00",
  motorise: "10-121104-00-00",
  infanterie_legere: "10-121100-00-19",
  aeroporte: "10-121100-00-01",
  hybride: "10-121000-00-00",
};

/** SIDC du symbole ennemi (identité hostile, sans échelon) d'un type de forces dominant ; interarmes à défaut. */
export function enemySidc(type: CustomEnemySheet["forces"]["type_dominant"] | null | undefined): string {
  return sidcOf({ side: "ENI", app6: TYPE_APP6[type ?? "hybride"] ?? TYPE_APP6.hybride, echelon: null })!;
}
